'use client';

import { useAuth, useOrganization } from '@clerk/nextjs';
import { AblyProvider } from 'ably/react';
import dynamic from 'next/dynamic';
import { createContext, useContext, useEffect, useState } from 'react';
import { getAblyClient } from './client';

/** Client key for staff with no active organization, so /admin/* still gets a connection. */
const ADMIN_CLIENT_KEY = 'admin';

const AblyReadyContext = createContext(false);

/** True when AblyProvider is mounted and ChannelProviders can safely render. */
export const useAblyReady = () => useContext(AblyReadyContext);

const RealTimeProvider = ({ children }: { children: React.ReactNode }) => {
  const { organization } = useOrganization();
  const { sessionClaims } = useAuth();
  const [client, setClient] = useState<ReturnType<typeof getAblyClient> | null>(null);

  const isAdmin = sessionClaims?.metadata.isAdmin === true;
  const orgId = organization?.id ?? (isAdmin ? ADMIN_CLIENT_KEY : null);

  // Swap the Ably client in an effect, never during render. getAblyClient closes
  // the previous connection when the org changes; doing that in render fires a
  // setState inside still-mounted channel subscribers (e.g. DevicesMap) and
  // crashes the tree ("Cannot update a component while rendering"), which is the
  // sporadic client-side exception seen when switching organizations. useAblyReady
  // keeps subscribers from mounting until the client exists.
  useEffect(() => {
    setClient(orgId ? getAblyClient(orgId) : null);
  }, [orgId]);

  if (!client) return <>{children}</>;

  return (
    <AblyProvider client={client}>
      <AblyReadyContext.Provider value={true}>{children}</AblyReadyContext.Provider>
    </AblyProvider>
  );
};

export default dynamic(() => Promise.resolve(RealTimeProvider), {
  ssr: false,
});
